/**
 * useFeatures hook
 *
 * Provides feature checking for the current user based on:
 * 1. Admin status (admins have all features)
 * 2. Role-based feature overrides from organization.features.roles[roleId]
 * 3. Organization-level feature defaults
 * 4. Default (true if not explicitly disabled)
 */

/**
 * Get a nested value from an object using dot notation
 * @param {Object} obj - The object to traverse
 * @param {string} path - Dot-separated path (e.g., "catalog.pdp.sms")
 * @returns {*} The value at the path, or undefined if not found
 */
function getNestedValue(obj, path) {
	if (!obj || !path) return undefined;
	const parts = path.split(".");
	let current = obj;
	for (const part of parts) {
		if (current && typeof current === "object" && part in current) {
			current = current[part];
		} else {
			return undefined;
		}
	}
	return current;
}

/**
 * Check if a feature is enabled for the current user
 * Supports both flat keys (e.g., "product_sharing.share_sms") and
 * nested paths (e.g., "catalog.pdp.sms")
 *
 * @param {string} featurePath - The feature path to check
 * @returns {boolean} - Whether the feature is enabled
 */
export function checkFeature(featurePath) {
	if (!featurePath) return true;

	try {
		const cachedMember = JSON.parse(localStorage.getItem("organization_member:data"));
		if (!cachedMember) return true;

		const roleId = cachedMember.role?.id;
		const orgFeatures = cachedMember.organization?.features || {};

		// Check role-level override from organization.features.roles[roleId]
		const roleFeatures = orgFeatures.roles?.[roleId];
		if (roleFeatures) {
			// First try flat key match
			if (featurePath in roleFeatures) {
				return roleFeatures[featurePath] !== false;
			}
			// Then try nested path
			const roleValue = getNestedValue(roleFeatures, featurePath);
			if (roleValue !== undefined) {
				return roleValue !== false;
			}
		}

		// Check organization-level default
		// First try flat key match
		if (featurePath in orgFeatures && featurePath !== "roles") {
			return orgFeatures[featurePath] !== false;
		}
		// Then try nested path
		const orgValue = getNestedValue(orgFeatures, featurePath);
		if (orgValue !== undefined) {
			return orgValue !== false;
		}

		// Default: enabled
		return true;
	} catch (e) {
		console.warn("Error checking feature:", e);
		return true;
	}
}

/**
 * Get the raw feature value (for non-boolean features)
 * @param {string} featurePath - The feature path
 * @returns {*} The feature value
 */
export function getFeatureValue(featurePath) {
	if (!featurePath) return undefined;

	try {
		const cachedMember = JSON.parse(localStorage.getItem("organization_member:data"));
		if (!cachedMember) return undefined;

		const roleId = cachedMember.role?.id;
		const orgFeatures = cachedMember.organization?.features || {};

		// Check role-level override
		const roleFeatures = orgFeatures.roles?.[roleId];
		if (roleFeatures) {
			if (featurePath in roleFeatures) {
				return roleFeatures[featurePath];
			}
			const roleValue = getNestedValue(roleFeatures, featurePath);
			if (roleValue !== undefined) {
				return roleValue;
			}
		}

		// Check organization-level
		if (featurePath in orgFeatures && featurePath !== "roles") {
			return orgFeatures[featurePath];
		}
		return getNestedValue(orgFeatures, featurePath);
	} catch (e) {
		console.warn("Error getting feature value:", e);
		return undefined;
	}
}

/**
 * Get all features for a specific category (e.g., "catalog.pdp")
 * Returns the merged result of org defaults + role overrides
 * @param {string} categoryPath - The category path
 * @returns {Object} The merged features object
 */
export function getCategoryFeatures(categoryPath) {
	if (!categoryPath) return {};

	try {
		const cachedMember = JSON.parse(localStorage.getItem("organization_member:data"));
		if (!cachedMember) return {};

		const roleId = cachedMember.role?.id;
		const orgFeatures = cachedMember.organization?.features || {};

		// Get org-level category features
		const orgCategoryFeatures = getNestedValue(orgFeatures, categoryPath) || {};

		// Get role-level category features
		const roleFeatures = orgFeatures.roles?.[roleId];
		const roleCategoryFeatures = roleFeatures ? getNestedValue(roleFeatures, categoryPath) || {} : {};

		// Merge: role overrides take precedence
		return { ...orgCategoryFeatures, ...roleCategoryFeatures };
	} catch (e) {
		console.warn("Error getting category features:", e);
		return {};
	}
}

/**
 * React hook for feature checking
 */
export function useFeatures() {
	return {
		checkFeature,
		isEnabled: checkFeature,
		getFeatureValue,
		getCategoryFeatures,

		// Catalog modes
		buyingModeEnabled: checkFeature("catalog.modes.buying"),
		wholesaleCostEnabled: checkFeature("catalog.modes.wholesale"),

		// Common feature checks
		bookmarksEnabled: checkFeature("catalog.lists.bookmarks"),
		downloadEnabled: checkFeature("catalog.pdp.download"),
		smsEnabled: checkFeature("catalog.pdp.sms"),
		emailEnabled: checkFeature("catalog.pdp.email"),
		shareEnabled: checkFeature("catalog.pdp.share"),
		qrEnabled: checkFeature("catalog.pdp.qr"),
		copyEnabled: checkFeature("catalog.pdp.copy"),
		newWindowEnabled: checkFeature("catalog.pdp.new-window"),
		inquiryEnabled: checkFeature("catalog.pdp.inquiry"),

		// Gallery/list sharing
		listSmsEnabled: checkFeature("catalog.lists.sms"),
		listEmailEnabled: checkFeature("catalog.lists.email"),

		// Permission-related features
		switchAssociateEnabled: checkFeature("catalog.permissions.switch_associate"),
		switchOrganizationEnabled: checkFeature("catalog.permissions.switch_organization"),
	};
}

export default useFeatures;
